// agentReview.mjs — agent 预读面的纯逻辑（SB-078 L2，第六十六批）。
//
// ── 呈现次序是一条裁决，不是排版偏好 ────────────────────────────────────────
// 需求方本批裁「**问题在前，建议默认折叠**」。理由：**锚定效应是真的且很强**，而这个
// agent 的建议质量**一次都还没被验证过**。审批人先看到「同意」两个字，再去读工单，
// 与先读工单再看建议，得出的结论不是同一件事。
//
// 等它累积出准确率之后，把建议提前只是改一个默认值；反过来很难——人一旦习惯了点
// "同意"就不再读了。故本模块把次序与折叠态**做成数据**，而不是散在 JSX 里。
//
// ── 前端要再排一次推荐项，因为提示词只是请求 ────────────────────────────────
// 系统提示要求模型把推荐项排在最前，但**提示词是请求不是保证**（同 polish_guard 的
// 立场：散文规则一律要有机器判据兜底）。这里按 recommended 再排一次，且是**稳定排序**
// ——同为推荐/同为非推荐的选项保持模型给的相对次序，那个次序本身是有信息的。

// 两类问题的受众不同（见 ticket_review.h「两类问题」）：
//   · questions    → 审批人，需要**判断**
//   · askSubmitter → 提交人，缺**事实**
// 混在一起的后果很具体：审批人看到一堆自己答不了的问题，然后学会忽略整块内容。
// ⚠ 第一百一十批坐实的旧洞（本批一并修）：这只函数**漏了 toolTrace**，而
// AgentReviewPanel.jsx 判的是 `Array.isArray(review?.toolTrace)` —— 恒 false，于是
// 第九十三批的「预读查阅了什么」那一块**从上线起就一次都没渲染过**。它不只是少一块
// UI：那是仓内容注入面的第二道防线（agent 读过什么审批人看得见），而设计文档
// `ai-review-surface.md §10` 把它记为已落地。
// 病灶的形状是「服务端投影多一个键，这里忘了收，而忘了不响」——本批要往同一只函数
// 里再加两组字段（workItemDraft / directives），同一个坑会被再踩两次。所以补的不只是
// 那一行：`agentReview.test.mjs` 里有一条**投影对账**用例，拿一份逐字照抄服务端
// `review_json()`（routes_approver.cpp）的样例钉住「顶层键要么被收下、要么在豁免表里
// 逐条写明理由」。下一个加字段的人不必记得这条纪律，忘了会当场红。
export function normalizeReview(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const questions = (Array.isArray(raw.questions) ? raw.questions : [])
    .map(normalizeQuestion)
    .filter(Boolean);
  const askSubmitter = (Array.isArray(raw.askSubmitter) ? raw.askSubmitter : [])
    .filter((s) => typeof s === 'string' && s.trim() !== '');
  return {
    questions,
    askSubmitter,
    summary: typeof raw.summary === 'string' ? raw.summary : '',
    recommendation: typeof raw.recommendation === 'string' ? raw.recommendation : '',
    reasoning: typeof raw.reasoning === 'string' ? raw.reasoning : '',
    // 服务端给的默认折叠态。缺字段时**默认折叠**——保守的那一侧是"不锚定"。
    recommendationCollapsed: raw.recommendationCollapsed !== false,
    model: typeof raw.model === 'string' ? raw.model : '',
    toolTrace: normalizeToolTrace(raw.toolTrace),
    // 第一百一十批 D：工作项草案。null = agent 没有建议建工作项（服务端的空对象
    // `{}` 读到这里也是 null——DP-10 那条"空对象不是一个可采纳的草案"）。
    workItemDraft: normalizeDraft(raw.workItemDraft),
    // 第一百一十批 E：**这一轮是带着什么命令跑出来的**。一份被要求「出工作项草案」
    // 的意见里出现草案，与一份自发出草案的意见，可信度不是一回事——所以它必须
    // 原样显示在审批面上，而不只是留在库里。
    directives: normalizeDirectives(raw.directives),
    // 第一百一十一批 D（OQ-C50）：**这一组给提交人的问题的身份**，服务端算好的。
    // 原样收下、原样回传，一个字符都不加工——见 askKeyOf 的头注。
    askKey: typeof raw.askKey === 'string' ? raw.askKey : '',
    updatedAtMs: Number(raw.updatedAtMs) || 0,
  };
}

// 工具留痕。ok 缺席读作 false：一次"不知道成没成"的取材不该显示成成功的那一格。
function normalizeToolTrace(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((c) => c && typeof c === 'object')
    .map((c) => ({
      tool: typeof c.tool === 'string' ? c.tool : '',
      args: typeof c.args === 'string' ? c.args : '',
      note: typeof c.note === 'string' ? c.note : '',
      ok: c.ok === true,
      bytes: Number(c.bytes) || 0,
    }));
}

function normalizeQuestion(q) {
  if (!q || typeof q !== 'object') return null;
  // questionKey 由服务端给（题面哈希）。缺了就不显示这道题——没有 key 就没法作答，
  // 而一道点得动却提交不了的题，比不显示更糟。
  if (typeof q.questionKey !== 'string' || q.questionKey === '') return null;
  const options = (Array.isArray(q.options) ? q.options : [])
    .filter((o) => o && typeof o.label === 'string' && o.label !== '')
    .map((o) => ({
      label: o.label,
      description: typeof o.description === 'string' ? o.description : '',
      recommended: Boolean(o.recommended),
    }));
  // 少于两个选项的题在界面上点不动（后端也丢，这里是同一条件的第二份——
  // 界面比接口宽或窄都出过事，DP-2）。
  if (options.length < 2 || typeof q.question !== 'string' || q.question === '') return null;
  return {
    questionKey: q.questionKey,
    header: typeof q.header === 'string' ? q.header : '',
    question: q.question,
    multiSelect: Boolean(q.multiSelect),
    allowOther: q.allowOther !== false,
    options: sortRecommendedFirst(options),
  };
}

// 稳定排序：推荐项提到最前，其余保持原有相对次序。
export function sortRecommendedFirst(options) {
  return [
    ...options.filter((o) => o.recommended),
    ...options.filter((o) => !o.recommended),
  ];
}

// 这份预读值不值得占一块地方。**没有问题、也没有摘要**时不显示——
// 一个空面板比没有面板更糟：它看起来像加载失败。
export function hasSomethingToShow(review) {
  if (!review) return false;
  return review.questions.length > 0 || review.askSubmitter.length > 0 ||
         review.summary !== '';
}

// 列表页每行的徽标文本判据。0 不显示徽标——"0 个问题"是噪音。
export const questionBadgeCount = (row) =>
  Number(row && row.reviewQuestions) > 0 ? Number(row.reviewQuestions) : 0;

// ── SB-076 期二（第六十六批 L3）：作答 ──────────────────────────────────────
//
// **每道题都必须答**才能提交。这不是形式主义：agent 之所以问，是因为它需要这个判断
// 才知道下一步该做什么（L4 落实面据此行动）。裁了却跳过问题，等于让 agent 拿着一个
// 缺口去执行。而"答不上来"这种情况有出口——**每道题都带 other 简答**，
// 所以"必须答"不会把任何人卡住。
//
// 校验放在纯模块里而不是组件里：它同时是"提交按钮亮不亮"与"缺哪几题"的判据，
// 两处用同一份，且能不起浏览器地测。

export function emptyAnswers(review) {
  const out = {};
  for (const q of (review?.questions ?? [])) out[q.questionKey] = { labels: [], other: '' };
  return out;
}

// 一道题算不算答了：选了至少一个选项，**或**写了 other 简答。
export function isAnswered(a) {
  if (!a) return false;
  return (Array.isArray(a.labels) && a.labels.length > 0) ||
         (typeof a.other === 'string' && a.other.trim() !== '');
}

// 还缺哪几题（返回 questionKey 数组）。界面据此告诉人**还差什么**，
// 而不是给一个说不出理由的灰按钮——一个不解释自己为何不可点的按钮，
// 人只会以为页面坏了。
export function missingAnswers(review, answers) {
  return (review?.questions ?? [])
    .filter((q) => !isAnswered(answers?.[q.questionKey]))
    .map((q) => q.questionKey);
}

export const canSubmit = (review, answers, action) =>
  Boolean(action) && missingAnswers(review, answers).length === 0;

// 单选题的选择：**替换**而不是追加。多选题才累积。
export function toggleOption(answer, label, multiSelect) {
  const cur = Array.isArray(answer?.labels) ? answer.labels : [];
  if (!multiSelect) return { ...answer, labels: cur[0] === label ? [] : [label] };
  return { ...answer,
           labels: cur.includes(label) ? cur.filter((l) => l !== label) : [...cur, label] };
}

// ── 第九十九批（SB-085 双向译题）：显示映射 ─────────────────────────────────
// questionKey → 译文题面。**只做显示**：作答状态（toggleOption 的 labels）与
// 回传（toPayload）永远落在原文对象上——台账存原文为正本是已裁原则，译文要是
// 进了答案，裁决记录里就会出现一份没人签发过的文本。
export function i18nQuestionMap(raw, uiLang) {
  const pack = raw?.questionsI18n?.[uiLang];
  if (!pack || !Array.isArray(pack.questions)) return null;
  const out = {};
  for (const q of pack.questions) {
    if (typeof q?.questionKey !== 'string' || q.questionKey === '') continue;
    // **与 normalizeQuestion 同一个排序**（推荐项前置、稳定）：原题在显示前经
    // sortRecommendedFirst 重排，译文若保持服务端原序，按下标配对就会张冠李戴
    // （对抗审查坐实：推荐项不在首位时，译文标签落到别的选项上）。译文行的
    // recommended 由 schema 钉住逐位复制、cpp 侧同构校验再核一遍，故同一比较器
    // 必然产生同一置换。
    const rawOptions = Array.isArray(q.options)
      ? q.options.map((o) => ({
          label: typeof o?.label === 'string' ? o.label : '',
          description: typeof o?.description === 'string' ? o.description : '',
          recommended: Boolean(o?.recommended),
        }))
      : [];
    out[q.questionKey] = {
      question: typeof q.question === 'string' ? q.question : '',
      header: typeof q.header === 'string' ? q.header : '',
      options: sortRecommendedFirst(rawOptions),
    };
  }
  return Object.keys(out).length > 0 ? out : null;
}

// 单题的显示文本：有译文用译文，缺哪个字段回落原文——半份译文不至于开天窗。
// 选项按下标对位（服务端同构校验保证数目一致）；返回的 options 只含显示字段，
// 交互仍用原题的 options（label 原文、recommended 原判）。
export function displayQuestion(q, map) {
  const t = map?.[q.questionKey];
  if (!t) {
    return { question: q.question, header: q.header,
             options: q.options.map((o) => ({ label: o.label, description: o.description })),
             translated: false };
  }
  return {
    question: t.question || q.question,
    header: t.header || q.header,
    options: q.options.map((o, i) => ({
      label: t.options[i]?.label || o.label,
      description: t.options[i]?.description || o.description,
    })),
    translated: true,
  };
}

// 请求体。questionKey 原样回传——服务端据它核对"你答的还是不是屏幕上那道题"
// （预读可能在你读页面之后被重跑过）。
export function toPayload(review, answers, action, note, opts) {
  const body = {
    action,
    note: note ?? '',
    answers: (review?.questions ?? []).map((q) => ({
      questionKey: q.questionKey,
      labels: answers?.[q.questionKey]?.labels ?? [],
      other: answers?.[q.questionKey]?.other ?? '',
    })),
  };
  // 第一百一十批 D：采纳草案。**只在 accept 上发**——服务端对
  // `adoptDraft ∧ action≠accept` 回 400 RULE_VIOLATION（工作项只在受理时建），
  // 而这里是同一条判据的第二份：勾选框在动作不是受理时本就不可勾（面板按
  // `adoptChecked()` 渲染，勾了再改动作会当场取消勾选），所以这一行防的不是用户，
  // 是**状态漂移**——两处都不写的话，一次"先勾再改动作"的提交会去吃一个 400，
  // 而那个 400 的话术说的是一件用户并不知道自己做过的事。
  // 字段**默认不出现**而不是发 `false`：契约的默认值是保守侧，少发一个键比多发一个
  // 说"别采纳"的键更难被误读。
  if (opts?.adoptDraft && action === 'accept') {
    body.adoptDraft = true;
    // 处置①：连同**你读的是哪一份草案**一起发。服务端对不上就 409 STALE_DRAFT，
    // 与题面对不上时 STALE_QUESTION 是同一条判据——此前答案被钉死而草案裸奔，
    // 而那份草案落进去就是计费台账的锚点。
    // 草案缺 key（旧后端）时发空串：服务端算出来的键非空，于是它会退回而不是
    // **静静采纳一份来历不明的草案**。fail-closed 是这一侧唯一说得通的方向。
    body.draftKey = review?.workItemDraft?.draftKey ?? '';
  }
  // ── 第一百一十一批 D（OQ-C50）：按 agent 的问题发 request_info ──────────────
  // **只在 `request_info` 上发**，与 adoptDraft 只在 accept 上发是同一条纪律：
  // 这几条问的是**提交人**，跟着受理/不受理发出去就成了一段没有收件人的话
  //（服务端对 `infoRequestSource ∧ action≠request_info` 回 400）。
  //
  // ⚠ 它**不改变发出去的文本**——发出去的永远是 `note`，即审批人编辑之后那一版。
  // 这一位只决定服务端要不要把 agent 的原文逐字留一份痕：「agent 问了什么」与
  // 「人最终问了什么」是两件事，而前者是评估这个 agent 有没有用的唯一数据。
  if (opts?.usedAskDraft && action === 'request_info') {
    body.infoRequestSource = 'agent_ask';
    body.askKey = askKeyOf(review);
  }
  return body;
}

// ── 第一百一十一批 D：这一组给提交人的问题的身份 ─────────────────────────────
// **服务端算，前端只搬运**（与 `workItemDraft.draftKey` 逐字同款）。刻意不在前端
// 重算一份 sha256：`normalizeReview` 会把 askSubmitter 里的空串与非字符串项过滤掉
//（它渲染不了那些），于是"前端算的键"覆盖的清单与服务端那一份**不是同一个清单**
// ——agent 哪天吐出一个空字符串项，两侧就永久对不上，而症状是每一次按草案发
// request_info 都吃 409 STALE_ASK，且那句 409 说的是「agent 重跑过」：一句与事实
// 无关的话。一份实现、一处真理。
//
// 缺席（旧后端 / 历史轮次）读作空串：服务端算出来的键非空，于是它会**退回**而不是
// 静静发出一组来历不明的问题。fail-closed 是这一侧唯一说得通的方向。
export const askKeyOf = (review) =>
  (typeof review?.askKey === 'string' ? review.askKey : '');

// ── agent 的问题 → 一段可以直接当 note 发出去的正文 ──────────────────────────
// **不是排版偏好**：这段文字会原样落进 `ticket_feedback`，是提交人唯一看得到的东西。
// 抬头 + 逐条编号 + 收尾，键位取**界面语言**（打字的是审批人，读的是他自己写下的
// 这一段；工单语言是提交人那一侧的事，与这里无关）。
//
// 空数组 ⇒ 返回 `''`，**不是一段"没有问题"的客套话**：那样一段话发出去等于浪费一次
// request_info 的空批注，比按钮不亮更坏——提交人会以为被问了什么，然后回一句"没有"。
//
// `t` 由调用方传入（同 attachments.mjs 的 attachKindsText(t) 那条既有姿态）：话术走
// 词典、判据留在这里，两件事各在各的地方。
export function askDraftText(askSubmitter, t) {
  const list = (Array.isArray(askSubmitter) ? askSubmitter : [])
    .filter((s) => typeof s === 'string' && s.trim() !== '');
  if (list.length === 0) return '';
  const lines = list.map((s, i) => `${i + 1}. ${s.trim()}`);
  return [t('agentReview.ask.header'), ...lines, t('agentReview.ask.footer')].join('\n');
}

// 「按 agent 的问题请求补充」那个按钮的三态。与 draftAdoptState 同形。
//
// ⚠ `needsRequestInfo` 是**信息**不是阻拦：审批人多半先读问题、最后才选动作，
// 一个在他读问题时就灰着的按钮会被读成「不能这么做」——与勾选框那条完全同源
//（见 draftAdoptState 与 AgentReviewPanel 里那段）。所以这里 enabled 恒为 true，
// reason 只用来决定旁边那句话说什么。
//   show=false        没有问题 ⇒ 整块不出现（不是灰按钮 + tooltip）
//   willOverwrite     批注里已经有字了 ⇒ 点下去要先确认一次，绝不静默合并
//   needsRequestInfo  动作还不是「请求补充」⇒ 点这个按钮会替他切过去
export function askDraftState({ askSubmitter, action, noteDirty } = {}) {
  if (!askSubmitter?.length) return { show: false, enabled: false, reason: '' };
  if (noteDirty) return { show: true, enabled: true, reason: 'willOverwrite' };
  if (action !== 'request_info') return { show: true, enabled: true, reason: 'needsRequestInfo' };
  return { show: true, enabled: true, reason: '' };
}

// 已有裁决的归一化。`stale` 的那些仍然显示——**一条过期的裁决不是没发生过**，
// 它是"当时这么裁的，后来地基变了"，而那句话本身就是要给人看的。
export function normalizeRulings(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((r) => ({
    round: Number(r.round) || 1,
    questionKey: String(r.questionKey ?? ''),
    question: String(r.question ?? ''),
    labels: Array.isArray(r.labels) ? r.labels : [],
    other: String(r.other ?? ''),
    // SB-147：显示名与邮箱并排（submitter_json 每行都带 email）；只有其一时用其一。
    ruledBy: r.ruledBy
      ? (r.ruledBy.displayName && r.ruledBy.email
          ? `${r.ruledBy.displayName} <${r.ruledBy.email}>`
          : (r.ruledBy.displayName || r.ruledBy.email || ''))
      : '',
    ruledAtMs: Number(r.ruledAtMs) || 0,
    stale: Boolean(r.stale),
    staleReason: String(r.staleReason ?? ''),
  }));
}

// 这道题已经被裁过了吗（先到先得之后，第二个人看到的必须是结论而不是空表单）。
export const rulingFor = (rulings, questionKey) =>
  (rulings ?? []).filter((r) => r.questionKey === questionKey && !r.stale).slice(-1)[0] ?? null;

// ════════════════════════════════════════════════════════════════════════════
// 第一百一十批 B：预读的**三态**、逃生口、人工再 grill、工作项草案
// ════════════════════════════════════════════════════════════════════════════
//
// ── ① 为什么「没跑 / 跑失败了 / 该类型不做预读」必须分得开 ────────────────────
// 到本批为止这三件事在页面上**逐像素相同**：`agentReview` 为 null ⇒ 整块不渲染。
// 于是审批人面对一张空白的工单时，无法区分「AI 还没读到它」「AI 读了三次都崩了」
// 「这类工单本来就不预读」——而三者的下一步动作完全不同（等 / 重跑 / 什么都不用做）。
// 一个说不出自己出了什么事的界面，会让人把三种情况都当成第三种，然后再也不看它。
//
// 判据全在服务端算好（`agentReviewStatus.state`，八态），**前端只做 state → i18n 键
// 的映射**。理由：这三态的地面真值在 `job` 表里（state / result_class / attempts），
// 前端重算一份就是第二份会漂的口径，而漂开的那一侧一定是"说了一句假话"。

export const REVIEW_STATES = Object.freeze([
  'not_applicable', 'never_queued', 'queued', 'running',
  'failed', 'skipped_quota', 'escalated', 'done',
]);

export const ESCALATION_REASONS = Object.freeze([
  'insufficient_context', 'out_of_scope', 'contradictory_instructions',
  'tooling_blocked', 'unsafe_request',
]);

// 状态的归一化。**永不返回 null**——但"说不出话"与"没事"是两回事：
// 旧后端（本批之前）根本不投影这个字段，值域外的 state 同理。两种情形都归到
// `state:''`，而 `''` 的呈现是**什么都不说**（不是"正常"），且 `canRetry:false`
// ——给一个旧后端画一个按下去必然 404 的重跑按钮，比不画更糟。
export function normalizeStatus(raw) {
  const known = raw && typeof raw === 'object' && REVIEW_STATES.includes(raw.state);
  if (!known) {
    return { state: '', round: 0, attempts: 0, maxAttempts: 0, failClass: '',
             lastAtMs: 0, nextRunAtMs: 0, canRetry: false, escalation: null };
  }
  return {
    state: raw.state,
    round: Number(raw.round) || 0,
    attempts: Number(raw.attempts) || 0,
    maxAttempts: Number(raw.maxAttempts) || 0,
    // ⚠ `''` 是「不知道」不是「正常」：本批之前的存量作业行没有分类，把它折进
    // transient 就是对着一条其实是 400 的失败说"多半是网络抖动，重试看看"，
    // 而那是三次徒劳的邀请。值域外同样收敛到 ''（下面出「原因不明」那一句）。
    // 第二百三十一批新增 `account`：账户此刻不让用（余额／配额／密钥失效）。
    // 它与 permanent 的处置相同而**对人的意思相反**——permanent 要人改指令，
    // account 要人去修账户然后原样重跑；把它折进 permanent 就是让屏幕对一次
    // 余额耗尽说"换个指令再试"，而那是一条做了也没用的建议。
    failClass: raw.failClass === 'transient' || raw.failClass === 'permanent'
             || raw.failClass === 'account'
      ? raw.failClass : '',
    lastAtMs: Number(raw.lastAtMs) || 0,
    nextRunAtMs: Number(raw.nextRunAtMs) || 0,
    // 能不能重跑是**服务端的判断**（它同时看工单终局性、类型是否预读、在途作业、
    // 当日次数）。前端不重算：这里少一个条件，用户按下去吃的是 409，而 409 的话术
    // 解释不了「这张单已经结案了」这种他一眼就该看到的事。
    canRetry: raw.canRetry === true,
    escalation: normalizeEscalation(raw.escalation),
  };
}

// 状态那一行的 i18n 键。返回 null ⇒ **不显示状态块**：
//   · `done` —— 意见本身就在下面，再说一句"已完成"是噪音；
//   · `''`  —— 旧后端／未知值域，说不出话就别编。
export function statusTextKey(status) {
  const st = status?.state;
  if (!st || st === 'done') return null;
  if (st !== 'failed') return `agentReview.status.${st}`;
  if (status.failClass === 'transient') return 'agentReview.status.failed.transient';
  if (status.failClass === 'permanent') return 'agentReview.status.failed.permanent';
  if (status.failClass === 'account')   return 'agentReview.status.failed.account';
  return 'agentReview.status.failed.unknown';
}

// 那一行的占位符。只有 transient 一句带 {n}（已试几次）——键与变量在同一处产出，
// 免得"改了文案忘了传变量"变成屏幕上一行印着 {n} 的字。
export function statusTextVars(status) {
  return statusTextKey(status) === 'agentReview.status.failed.transient'
    ? { n: String(status?.attempts ?? 0) } : undefined;
}

// 状态块的颜色。**not_applicable 不是警告**：那是这类工单的正常形态，用告警色
// 会把一件"本来就不该发生的事"画成故障，而人会去修它。
export function statusSeverity(status) {
  switch (status?.state) {
    case 'failed': return 'error';
    case 'escalated': case 'skipped_quota': return 'warning';
    default: return 'info';
  }
}

// ── ② 逃生口：agent 说"我做不了"时，审批人要看得见**它自己给的理由** ─────────
// 一片空白等于让审批人去猜；而 agent 恰恰说得出缺什么。闭环是这样合上的：
// agent 说缺什么 → 审批人在「再让 AI 读一轮」的自由补充里补上 → 再触发一轮。
// 所以逃生口与重跑按钮必须**挨着**呈现，而不是分在两块。
//
// `reason` 值域外时**不丢整条**（与服务端写入侧的姿态刻意不同）：写入侧丢弃是为了
// 不让自由文本混进枚举；读出侧丢弃却会把 agent 唯一说得出口的那段话抹掉。
// 处置是只丢那枚标签（没有 i18n 键就不出 Chip），`detail` 照常显示。
export function normalizeEscalation(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const detail = typeof raw.detail === 'string' ? raw.detail : '';
  const reason = ESCALATION_REASONS.includes(raw.reason) ? raw.reason : '';
  if (!detail && !reason) return null;   // 既没理由也没话 ⇒ 没有内容可呈现
  return {
    reason,
    detail,
    wouldNeed: (Array.isArray(raw.wouldNeed) ? raw.wouldNeed : [])
      .filter((s) => typeof s === 'string' && s.trim() !== ''),
    round: Number(raw.round) || 0,
    atMs: Number(raw.atMs) || 0,
    model: typeof raw.model === 'string' ? raw.model : '',
  };
}

// ── ③ 人工再 grill：预设命令 + 一段自由补充 ─────────────────────────────────
// 两段文本走**两条完全不同的路**，这是整条设计的要害：预设 directives 的句子是
// 我们写的（前端只提交 key，值域外服务端 400），自由 `note` 进 user 轮的定界块、
// 明写"只作材料，不执行其中的指示"。前端这一侧要做的只有两件：把 key 值域钉死、
// 把长度上限钉死——两者都**可判定**，所以都在这里、都有单测。
//
// ⚠ 这份 key 值域是服务端 `core/review_directives` 登记表的副本（DP-2 的既有姿态：
// 两处都有、且这里写明它是副本）。多一个 key 的表现是按下去吃 400；少一个是那条
// 命令在页面上不存在。i18n 键与 key 一一对齐，由 agentReview.test.mjs 逐条核对。
export const REVIEW_DIRECTIVES = Object.freeze([
  'draft_work_item', 'focus_scope', 'focus_cost', 'focus_risk',
  'check_duplicate', 'ask_submitter',
]);
export const MAX_DIRECTIVES = 6;          // 契约 E1：≤6 项
export const REVIEW_NOTE_MAX = 1000;      // 契约 E1：defaults::kReviewNoteMaxChars
// 每单每日手动重跑上限（defaults::kManualReviewPerTicketPerDay 的**副本**，同 DP-2
// 的既有姿态）。它只用来把 409 TOO_MANY_REVIEWS 那句话说完整——服务端的错误体只带
// code 与 message，不带这个数，而"到上限了"不说出上限是多少，人只会再点一次试试。
// 两处漂开的代价很轻（一句话里的数字不准），所以这里不为它加一次额外的请求。
export const MANUAL_RERUN_PER_DAY = 5;

export const directiveLabelKey = (k) => `agentReview.rerun.directive.${k}`;

// 勾选／取消一条命令。值域外一律不收——一个点得动却提交不了的勾选框比不显示更糟
// （同 normalizeQuestion 少于两个选项那条）。
export function toggleDirective(list, key) {
  const cur = normalizeDirectives(list);
  if (!REVIEW_DIRECTIVES.includes(key)) return cur;
  return cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
}

// 值域内、去重、保序。读（agentReview.directives / 历史行）与写（重跑表单）共用
// 这一只：两处各写一份过滤就是两份会漂的值域。
export function normalizeDirectives(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const k of raw) if (REVIEW_DIRECTIVES.includes(k) && !out.includes(k)) out.push(k);
  return out;
}

// 再 grill 表单的整份判据：能不能提交、缺什么、发什么。
// 与 workHoursFormState 同形（第八十五批的既有姿态）：页面只渲染它的输出。
// `note` trim 后为空 ⇒ **不发这个键**（契约：trim 后为空即视为未填），而不是发一个
// 空串——空串会在审计行里留下一条"审批人补充了：（空）"，而那条记录说的不是真的。
export function rerunFormState({ directives, note } = {}) {
  const picked = normalizeDirectives(directives);
  const text = String(note ?? '');
  const trimmed = text.trim();
  const problems = {};
  // 计数按**原文长度**（服务端按字符数判的也是原文）——按 trim 后的长度算，
  // 一段末尾贴了 200 个空格的文本会在页面上显示"没超"，然后吃一个 400。
  if (text.length > REVIEW_NOTE_MAX) problems.note = 'tooLong';
  if (picked.length > MAX_DIRECTIVES) problems.directives = 'tooMany';
  const payload = { directives: picked };
  if (trimmed !== '') payload.note = trimmed;
  return {
    problems,
    noteChars: text.length,
    canSubmit: Object.keys(problems).length === 0,
    payload,
  };
}

// 重跑端点的错误码 → i18n 键。三个 409 各有各的处置，所以各说各的：
// 「有一轮正在跑」要等、「这类不预读」要放弃、「今天按太多次了」要明天再来。
// 一句"操作失败"会让人反复点同一个按钮——第六十六批已经为此付过一次账。
// 第一百一十批处置②又加了第四条：**今天的余量留给自动预读了**。它与 tooMany 必须
// 分开说——那条讲的是"你在这张单上按太多次"（人做错了什么），这条讲的是"全实例的
// 额度快见底，新工单的自动预读优先"（人什么都没做错，只是要等）。合成一句的话，
// 一个第一次按这个按钮的人会被告知他按太多次了。
export const RERUN_ERR_KEYS = Object.freeze({
  REVIEW_IN_FLIGHT: 'agentReview.rerun.err.inFlight',
  NOT_REVIEWABLE: 'agentReview.rerun.err.notReviewable',
  TOO_MANY_REVIEWS: 'agentReview.rerun.err.tooMany',
  REVIEW_BUDGET_RESERVED: 'agentReview.rerun.err.budgetReserved',
});

// ── ④ 工作项草案：agent 提议，人按一下 ──────────────────────────────────────
// 需求方本批裁「agent 只提建议，人按一下」。工作项是**计费台账的锚点**，建错了直接
// 影响工时归属——所以这里每一条判据都偏保守：宁可少显示一个勾选框（代价：审批人
// 再点一次），也不要显示一个按下去会 400 的勾选框（代价：他以为自己采纳了）。
export const DRAFT_KINDS = Object.freeze(['bug', 'maintenance', 'tech_debt']);

// 草案的归一化。title / scope / kind 是服务端 schema 的**必填三项**，缺任何一项就
// 不是一份可采纳的草案 ⇒ 返回 null，界面上整块不出现。
// 空对象 `{}`（DP-10 的默认值，读作"agent 没有建议建工作项"）自然落在这一条里。
export function normalizeDraft(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const title = typeof raw.title === 'string' ? raw.title.trim() : '';
  const scope = typeof raw.scope === 'string' ? raw.scope.trim() : '';
  const kind = DRAFT_KINDS.includes(raw.kind) ? raw.kind : '';
  if (!title || !scope || !kind) return null;
  return {
    title,
    scope,
    kind,
    why: typeof raw.why === 'string' ? raw.why : '',
    stakeholders: (Array.isArray(raw.stakeholders) ? raw.stakeholders : [])
      .filter((s) => typeof s === 'string' && s.trim() !== ''),
    // 第一百一十批处置①：**你读的是哪一份草案**。与题面的 questionKey 同一条纪律
    // ——作答请求原样回传，服务端对不上就 409 STALE_DRAFT。
    // ⚠ 缺席读作空串（旧后端 / **历史轮次**）。历史里的草案刻意没有这个键，于是
    //   它连"采纳它"这个请求都拼不出来——与历史题没有 questionKey 是同一道保证。
    draftKey: typeof raw.draftKey === 'string' ? raw.draftKey : '',
  };
}

// 采纳勾选框的三态。**它自己说得出为什么不能勾**——一个灰着且不解释的勾选框，
// 与"这张单不能采纳草案"在屏幕上完全同形，而人只会以为页面坏了。
//   show=false     没有草案 ⇒ 整块不出现
//   blocked        这张单已经关联了工作项 ⇒ 服务端会忽略采纳并回 already_linked，
//                  所以前端先说清楚：不是你没勾，是这条已经有归属了
//   needsAccept    动作不是"受理" ⇒ 工作项只在受理时建（服务端 400 的同一条判据）
export function draftAdoptState({ draft, action, existingNo } = {}) {
  if (!draft) return { show: false, enabled: false, reason: '' };
  if (existingNo) return { show: true, enabled: false, reason: 'blocked' };
  if (action !== 'accept') return { show: true, enabled: false, reason: 'needsAccept' };
  return { show: true, enabled: true, reason: '' };
}

// 「这次提交到底会不会采纳草案」。这是**载荷侧**的判据，不是勾选框的显示态——
// 勾选框直接跟着 adopt 走（理由见 AgentReviewPanel 里那段：审批人多半先读草案、
// 最后才选动作，一个在他读草案时就灰着的勾选框会被当成"不能采纳"）。
// 勾了却选了不受理时，界面**当场说出来**（agentReview.err.draftNeedsAccept），
// 而这一只保证载荷与那句话一致：说了不会采纳，就真的不发 adoptDraft。
export const adoptChecked = (adopt, action) => Boolean(adopt) && action === 'accept';

// rule 端点的错误码表。散在 JSX 里的 byCode 字面量改一处漏一处，第九十九批付过账。
//
// ⚠ 两条草案相关的错误**服务端不给独立的码**：契约 E2 把它们都落成
// 400 `RULE_VIOLATION`，只有 message 不同（"no work-item draft to adopt" /
// "a work item is only created on accept"），而 message 是英文散文，不进 i18n。
// 这里仍然登记 NO_DRAFT / DRAFT_NEEDS_ACCEPT 两个键位：日后服务端若分出独立的码，
// 前端一个字都不用改。今天的分辨走下面那只 ruleByCode——**按我们自己发了什么判**，
// 不按 message 猜。按 message 猜是可以做的（ruleKey 对 `R-05:` 就是这么做的），
// 但那是拿一句随时会被改写的散文当判据，而这里有一个我们百分之百知道的事实：
// 这一次提交到底带没带 adoptDraft。
export const RULE_ERR_KEYS = Object.freeze({
  ALREADY_RULED: 'agentReview.err.alreadyRuled',
  STALE_QUESTION: 'agentReview.err.staleQuestion',
  // 第一百一十批处置①：草案在你读页面之后被换过了。与 STALE_QUESTION 并列而不是
  // 合成一句：人要做的事一样（重读再决定），但**要重读的东西不一样**。
  STALE_DRAFT: 'agentReview.err.staleDraft',
  // 第一百一十一批 D：给提交人的那几条问题在你读页面之后被换过了。与 STALE_DRAFT
  // **并列而不合并**：人要做的事一样（重读再决定），但要重读的东西不一样——
  // 一个是工作项草案，一个是给提交人的问题。合成一句，他会去重读错的那一块。
  STALE_ASK: 'agentReview.err.staleAsk',
  STALE_GROUND: 'agentReview.err.staleGround',
  CONFLICT: 'agentReview.err.conflict',
  NO_DRAFT: 'agentReview.err.noDraft',
  DRAFT_NEEDS_ACCEPT: 'agentReview.err.draftNeedsAccept',
});

// 这一次提交的 byCode 表。带了 adoptDraft 时，`RULE_VIOLATION` 只可能是"没有可采纳
// 的草案"——另一条（只在受理时建）在客户端根本发不出去（toPayload 只在 accept 上发）。
// **不带**时绝不映射：那时的 RULE_VIOLATION 是别的规则在说话，套上草案的话术就是
// 给人指错方向，而指错方向的错误提示比没有提示更贵。
export function ruleByCode(sentAdoptDraft) {
  if (!sentAdoptDraft) return RULE_ERR_KEYS;
  return { ...RULE_ERR_KEYS, RULE_VIOLATION: RULE_ERR_KEYS.NO_DRAFT };
}

// ── ⑤ 上一轮的历史 ─────────────────────────────────────────────────────────
// 「一份过期的预读比没有更糟」讲的是**默认呈现**（默认折叠、明标已被覆盖），不是
// "不许留存"：agent 上一轮建议过什么、人没有采纳，正是衡量建议准确率的原料。
//
// ⚠ 历史题里**刻意没有 questionKey**，这是「只能看不能答」的**机器保证**而不是样式：
// 没有 key 的题连提交都拼不出来（toPayload 按 questionKey 取值），而 rule 端点对不在
// 当前预读里的 key 一律 STALE_QUESTION。所以这里用一只**独立的**题面归一化，且它
// 从不产出 questionKey——单测正向反向各钉一条。
export function normalizeHistory(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((h) => h && typeof h === 'object')
    .map((h) => ({
      round: Number(h.round) || 0,
      summary: typeof h.summary === 'string' ? h.summary : '',
      recommendation: typeof h.recommendation === 'string' ? h.recommendation : '',
      reasoning: typeof h.reasoning === 'string' ? h.reasoning : '',
      questions: (Array.isArray(h.questions) ? h.questions : [])
        .map(normalizeHistoryQuestion).filter(Boolean),
      askSubmitter: (Array.isArray(h.askSubmitter) ? h.askSubmitter : [])
        .filter((s) => typeof s === 'string' && s.trim() !== ''),
      workItemDraft: normalizeDraft(h.workItemDraft),
      directives: normalizeDirectives(h.directives),
      model: typeof h.model === 'string' ? h.model : '',
      reviewedAtMs: Number(h.reviewedAtMs) || 0,
      supersededAtMs: Number(h.supersededAtMs) || 0,
      supersededByRound: Number(h.supersededByRound) || 0,
    }));
}

// 历史题面：**没有 questionKey**，也不做推荐项前置重排——历史是"当时给你看的那一
// 份"，按今天的比较器重排一遍它就不再是当时那一份了。
function normalizeHistoryQuestion(q) {
  if (!q || typeof q !== 'object') return null;
  if (typeof q.question !== 'string' || q.question === '') return null;
  const options = (Array.isArray(q.options) ? q.options : [])
    .filter((o) => o && typeof o.label === 'string' && o.label !== '')
    .map((o) => ({
      label: o.label,
      description: typeof o.description === 'string' ? o.description : '',
      recommended: Boolean(o.recommended),
    }));
  if (options.length < 2) return null;
  return {
    header: typeof q.header === 'string' ? q.header : '',
    question: q.question,
    multiSelect: Boolean(q.multiSelect),
    allowOther: q.allowOther !== false,
    options,
  };
}

// ── ⑥ 审批队列每行的预读徽标 ────────────────────────────────────────────────
// 「一切正常」不该占像素（同 questionBadgeCount 的 0 不显示）。所以只有三种状态出
// 徽标，判据就是**i18n 里有没有这一条**：failed（有东西坏了）／ escalated（agent 说
// 它做不了）／ queued（AI 还在读，别急着裁）。其余一律不出——包括 running：它与
// queued 在审批人的下一步动作上没有分别，两枚徽标只会让人去分辨一件不影响他的事。
export const REVIEW_BADGE_STATES = Object.freeze(['failed', 'escalated', 'queued']);

export function reviewBadge(row) {
  const st = row && row.reviewState;
  if (!REVIEW_BADGE_STATES.includes(st)) return null;
  return {
    state: st,
    key: `approvals.reviewState.${st}`,
    color: st === 'failed' ? 'error' : st === 'escalated' ? 'warning' : 'default',
  };
}
